import React from 'react';
import { UrlSlugsUtil } from '@axiom/utils';
import type {
  CandidateIndustry,
  AfcCandidateSearch,
  User,
} from '@axiom/validation';
import {
  Card,
  CardSection,
  CardFooter,
  Button,
  Well,
  ImageCircle,
  Layout,
  CandidateProfileUtil,
  ParagraphHeader,
  CondensedLarge,
  Gutter,
  CondensedHeader,
  useApi,
  LightTheme,
  TaxonomyUtil,
  BusinessTitlesUtil,
} from '@axiom/ui';
import { PossibleImageSizes } from '@axiom/const';

import { TaxonomyApi } from '../../api/taxonomy-api';
import { PricingUtil } from '../../utils/pricing-util';
import { TalentCarouselBadge } from '../TalentCarouselBadge/TalentCarouselBadge';
import { CandidateApi } from '../../api/candidate-api';

import { DiscoverTalentCarouselEngagements } from './DiscoverTalentCarouselEngagements';
import { DiscoverTalentCarouselExperiences } from './DiscoverTalentCarouselExperiences';
import { DiscoverTalentCarouselIndustry } from './DiscoverTalentCarouselIndustry';
import { DiscoverTalentCarouselYearsOfExperience } from './DiscoverTalentCarouselYearsOfExperience';

export const DiscoverTalentCarouselCard = ({
  candidate,
  industryValue = null,
  user,
}: {
  candidate: AfcCandidateSearch;
  industryValue?: CandidateIndustry['industryValue'] | null;
  user: User;
}) => {
  const [{ data: taxonomy }] = useApi(TaxonomyApi.readTaxonomy());
  const { industries, practiceAreas } = taxonomy;

  const industry = candidate.industries?.find(
    ind => ind.industryValue === industryValue
  );
  const { fullTimeDailyRate, adHocHourlyRate, pricingAvailable } =
    PricingUtil.getPricingData(candidate, user);

  const handleFavoriteClick = async () => {
    await CandidateApi.createSavedCandidate(candidate.id);

    await Promise.all([
      CandidateApi.refreshFavoriteCandidates(),
      CandidateApi.refreshCandidateSearchResults(),
    ]);
  };

  const formattedLegalRole = BusinessTitlesUtil.locationConversion({
    businessTitle:
      TaxonomyUtil.getBusinessTitle(taxonomy, candidate.businessTitle)
        ?.displayName ?? null,
    candidate,
  });

  return (
    <Card name="DISCOVER_TALENT_CAROUSEL_CARD" stretched>
      <CardSection divider>
        <Well background="blue">
          <Layout horizontalGutter="16px" position="middle">
            <div>
              <ImageCircle
                name="DISCOVER_TALENT_IMAGE"
                size="small"
                background="light blue"
                imageName={candidate.calculatedDisplayName}
                src={CandidateProfileUtil.getProfileImageUri(
                  candidate,
                  PossibleImageSizes.W_100
                )}
              />
            </div>
            <Layout direction="vertical" verticalGutter="8px">
              <ParagraphHeader name="DISCOVER_TALENT_NAME">
                {candidate.calculatedDisplayName}
              </ParagraphHeader>
              <LightTheme backgroundName="none">
                <TalentCarouselBadge
                  name="TALENT_CAROUSEL_BADGE"
                  candidate={candidate}
                />
              </LightTheme>
            </Layout>
          </Layout>
          <Gutter bottom="16px" />

          <Layout verticalGutter="16px" direction="vertical">
            <Layout horizontalGutter="16px">
              <DiscoverTalentCarouselYearsOfExperience
                name="YEARS_OF_EXPERIENCE"
                candidate={candidate}
              />
            </Layout>
            {industry?.industryValue ? (
              <Layout horizontalGutter="16px">
                <DiscoverTalentCarouselIndustry
                  name="INDUSTRY"
                  industry={industry}
                  industries={industries}
                />
              </Layout>
            ) : null}
            <DiscoverTalentCarouselEngagements
              name="ENGAGEMENTS"
              candidate={candidate}
            />
          </Layout>
        </Well>
        <Gutter bottom="16px" />
        {!!candidate.primaryPracticeArea && (
          <Gutter bottom="16px">
            <CondensedHeader>Primary Practice Area</CondensedHeader>
            <CondensedLarge name="DISCOVER_PRACTICE_AREA">
              {TaxonomyUtil.formatPracticeAreaFocusAreaForDisplay(
                taxonomy,
                candidate.primaryPracticeArea,
                candidate.primaryFocusArea
              )}
            </CondensedLarge>
          </Gutter>
        )}
        {formattedLegalRole ? (
          <Gutter bottom="16px">
            <CondensedHeader>Legal Role</CondensedHeader>
            <CondensedLarge name="DISCOVER_PRACTICE_AREA">
              {formattedLegalRole}
            </CondensedLarge>
          </Gutter>
        ) : null}
        {!!pricingAvailable && (
          <Gutter bottom="16px">
            <Well>
              <CondensedHeader>Rate:</CondensedHeader>
              <CondensedLarge name="DISCOVER_RATE">
                Starting at {fullTimeDailyRate} for a five-day week
                {!!adHocHourlyRate &&
                candidate.primaryPracticeArea &&
                practiceAreas[candidate.primaryPracticeArea]?.fullName !==
                  practiceAreas.LegalSupport?.fullName
                  ? `; Up to ${adHocHourlyRate} for ad hoc hourly`
                  : null}
              </CondensedLarge>
            </Well>
          </Gutter>
        )}
        <DiscoverTalentCarouselExperiences
          name="EXPERIENCES"
          candidate={candidate}
        />
      </CardSection>
      <CardFooter>
        <Button
          onClick={handleFavoriteClick}
          icon="star"
          variation="outline"
          name="SAVE_TALENT_BUTTON"
        >
          Save
        </Button>
        <Button
          toTab={`/browse-talent/${UrlSlugsUtil.generateCandidateSlug(
            candidate,
            taxonomy
          )}`}
          name="VIEW_TALENT_BUTTON"
        >
          View Talent
        </Button>
      </CardFooter>
    </Card>
  );
};
